import { Task, TaskPriorityOptions } from '@griply/types';
import { TreeInstance } from '@headless-tree/core';
import { useUpdateTasks } from 'features/tasks';
import React, { useRef } from 'react';
import { InternalTreeItem } from 'shared/components/ui/virtualized-tree-list';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { getTaskIdFromTreeId } from 'shared/utils/tree-id-helpers';

enum Digits {
  One = 'Digit1',
  Two = 'Digit2',
  Three = 'Digit3',
  Four = 'Digit4',
}

const digitPriorityMap: Record<Digits, TaskPriorityOptions> = {
  [Digits.One]: TaskPriorityOptions.High,
  [Digits.Two]: TaskPriorityOptions.Medium,
  [Digits.Three]: TaskPriorityOptions.Low,
  [Digits.Four]: TaskPriorityOptions.None,
};

const priorityShortcutKeys = [
  ['ShiftLeft', Digits.One],
  ['ShiftRight', Digits.One],
  ['ShiftLeft', Digits.Two],
  ['ShiftRight', Digits.Two],
  ['ShiftLeft', Digits.Three],
  ['ShiftRight', Digits.Three],
  ['ShiftLeft', Digits.Four],
  ['ShiftRight', Digits.Four],
];

export const useTaskPriorityShortcuts = (
  tree: TreeInstance<InternalTreeItem> | undefined,
  tasks: Task[],
  updateTasks: ReturnType<typeof useUpdateTasks>['submit'],
) => {
  const handlePriorityShortcut = React.useCallback(
    (e: KeyboardEvent, keys: string[]) => {
      if (!tree) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      // We need to fetch the latest selected items fresh
      const selectedItems = [...tree.getSelectedItems()];
      if (!selectedItems.length) {
        return;
      }

      const tasksToUpdate = selectedItems
        .map((item) => {
          const taskId = getTaskIdFromTreeId(item.getItemData().sourceId);
          return tasks.find(({ id }) => taskId === id);
        })
        .filter(Boolean) as Task[];

      const digitKey = keys.find((key) =>
        Object.values(Digits).includes(key as Digits),
      );
      const priority = digitKey
        ? digitPriorityMap[digitKey as Digits] ?? null
        : undefined;

      if (priority !== undefined) {
        updateTasks(
          tasksToUpdate.map((task) => ({
            ...task,
            priority,
          })),
        );
      }
    },
    [tasks, tree, updateTasks],
  );

  // We use a ref to the handler to ensure the callback passed to useKeysDown is stable
  const handlePriorityShortcutRef = useRef(handlePriorityShortcut);
  handlePriorityShortcutRef.current = handlePriorityShortcut;

  const stableHandlePriorityShortcut = React.useCallback(
    (e: KeyboardEvent, keys: string[]) => {
      handlePriorityShortcutRef.current(e, keys);
    },
    [],
  );

  useKeysDown(priorityShortcutKeys, stableHandlePriorityShortcut, {
    ignoreInputFields: true,
  });
};
